export type Severity = 'critical' | 'warning' | 'info' | 'offline' | 'normal'
export type ActivityStatus =
  | 'driving'
  | 'sleeper'
  | 'on-site'
  | 'breakdown'
  | 'rest'

export interface RouteStop {
  id: string
  address: string
  eta: string
  window: string
  status: 'completed' | 'pending' | 'at-risk'
  distance?: string
}

export interface CargoItem {
  id: string
  bol: string
  commodity: string
  weight: number // lbs
  pallets: number
  stopId: string
  status: 'loaded' | 'in-transit' | 'delivered'
}

export interface Driver {
  id: string
  name: string
  truckId: string
  truckHeight: string
  status: Severity
  activityStatus: ActivityStatus
  hosDriven: number // out of 11
  location: { x: number; y: number }
  route: RouteStop[]
  cargo: CargoItem[]
  lastPing?: string
  signalBars?: number // 0-4, signal strength
}

export interface Alert {
  id: string
  driverId: string
  severity: Severity
  title: string
  description: string
  timeContext: string
  actionHint: string
  type: 'hos' | 'bridge' | 'offline' | 'traffic' | 'general'
  affectedDriverIds?: string[]
  actions?: { label: string; icon: string }[]
}

export interface Delivery {
  id: string
  refNumber: string
  status: 'completed' | 'pending' | 'at-risk'
  destination: string
  eta: string
  window: string
  commodity: string
  weight: number
  pallets: number
  driverId: string
  driverName: string
  truckId: string
}

const baseDrivers: Driver[] = [
  {
    id: 'd-marcus',
    name: 'Marcus Johnson',
    truckId: 'T-1042',
    truckHeight: '13\'6"',
    status: 'critical',
    activityStatus: 'driving',
    hosDriven: 10.5,
    signalBars: 4,
    location: { x: 65, y: 35 },
    route: [
      {
        id: 's1',
        address: '1420 Industrial Pkwy',
        eta: '07:30',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2',
        address: '3200 Commerce Way',
        eta: '09:15',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3',
        address: '5800 Westpark Dr',
        eta: '11:00',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's4',
        address: '7400 Sam Houston Pkwy',
        eta: '13:15',
        window: '13:00 - 15:00',
        status: 'completed',
      },
      {
        id: 's5',
        address: '9100 Bellaire Blvd',
        eta: '15:00',
        window: '13:00 - 14:30',
        status: 'completed',
      },
      {
        id: 's6',
        address: '8800 Highway 6',
        eta: '16:45',
        window: '16:00 - 18:00',
        status: 'at-risk',
        distance: '22 mi',
      },
      {
        id: 's7',
        address: 'Return to Depot',
        eta: '17:30',
        window: 'N/A',
        status: 'pending',
        distance: '15 mi',
      },
    ],
    cargo: [
      {
        id: 'c1',
        bol: 'BOL-88421',
        commodity: 'Auto Parts — Brake Assemblies',
        weight: 8200,
        pallets: 6,
        stopId: 's1',
        status: 'delivered',
      },
      {
        id: 'c2',
        bol: 'BOL-88422',
        commodity: 'Industrial Bearings',
        weight: 12400,
        pallets: 8,
        stopId: 's2',
        status: 'delivered',
      },
      {
        id: 'c3',
        bol: 'BOL-88423',
        commodity: 'Steel Coil — Cold Rolled',
        weight: 18500,
        pallets: 4,
        stopId: 's3',
        status: 'delivered',
      },
      {
        id: 'c4',
        bol: 'BOL-88424',
        commodity: 'Hydraulic Cylinders',
        weight: 9600,
        pallets: 5,
        stopId: 's4',
        status: 'delivered',
      },
      {
        id: 'c5',
        bol: 'BOL-88425',
        commodity: 'Transmission Components',
        weight: 11200,
        pallets: 7,
        stopId: 's5',
        status: 'delivered',
      },
      {
        id: 'c6',
        bol: 'BOL-88426',
        commodity: 'Engine Block Assemblies',
        weight: 14800,
        pallets: 4,
        stopId: 's6',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-sarah',
    name: 'Sarah Chen',
    truckId: 'T-0921',
    truckHeight: '13\'6"',
    status: 'warning',
    activityStatus: 'driving',
    hosDriven: 4.2,
    signalBars: 0,
    location: { x: 25, y: 70 },
    lastPing: '4 min ago',
    route: [
      {
        id: 's1',
        address: '450 Westheimer Rd',
        eta: '10:15',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's2',
        address: '9920 Highway 6',
        eta: '~11:30',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3',
        address: '1500 Katy Fwy',
        eta: '~13:45',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4',
        address: '6200 Richmond Ave',
        eta: '~15:20',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c4',
        bol: 'BOL-77310',
        commodity: 'Pharmaceutical — Temp Controlled',
        weight: 4800,
        pallets: 10,
        stopId: 's1',
        status: 'delivered',
      },
      {
        id: 'c5',
        bol: 'BOL-77311',
        commodity: 'Medical Devices (Class II)',
        weight: 3200,
        pallets: 5,
        stopId: 's2',
        status: 'in-transit',
      },
      {
        id: 'c6',
        bol: 'BOL-77312',
        commodity: 'Lab Equipment — Fragile',
        weight: 2100,
        pallets: 3,
        stopId: 's3',
        status: 'loaded',
      },
      {
        id: 'c6b',
        bol: 'BOL-77313',
        commodity: 'Surgical Supplies',
        weight: 1800,
        pallets: 4,
        stopId: 's4',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-david',
    name: 'David Kim',
    truckId: 'T-2847',
    truckHeight: '13\'6"',
    status: 'critical',
    activityStatus: 'driving',
    hosDriven: 6.5,
    signalBars: 4,
    location: { x: 45, y: 50 },
    route: [
      {
        id: 's1',
        address: '770 Washington Ave',
        eta: '12:00',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's2',
        address: '100 Main St (via Industrial)',
        eta: '13:15',
        window: '13:00 - 15:00',
        status: 'at-risk',
      },
      {
        id: 's3',
        address: '500 Broad St',
        eta: '14:30',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's4',
        address: '2250 Elm St',
        eta: '15:45',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c7',
        bol: 'BOL-65200',
        commodity: 'Lumber — Treated 2x6',
        weight: 22000,
        pallets: 12,
        stopId: 's1',
        status: 'delivered',
      },
      {
        id: 'c8',
        bol: 'BOL-65201',
        commodity: 'Concrete Mix (80lb bags)',
        weight: 19200,
        pallets: 10,
        stopId: 's2',
        status: 'in-transit',
      },
      {
        id: 'c8b',
        bol: 'BOL-65202',
        commodity: 'Rebar — #4 Grade 60',
        weight: 16800,
        pallets: 8,
        stopId: 's3',
        status: 'loaded',
      },
      {
        id: 'c8c',
        bol: 'BOL-65203',
        commodity: 'Drywall Sheets (4x8)',
        weight: 14200,
        pallets: 6,
        stopId: 's4',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-elena',
    name: 'Elena Rodriguez',
    truckId: 'T-3310',
    truckHeight: '12\'0"',
    status: 'warning',
    activityStatus: 'on-site',
    hosDriven: 2.1,
    signalBars: 4,
    location: { x: 80, y: 20 },
    route: [
      {
        id: 's1',
        address: '3000 Post Oak Blvd',
        eta: '09:30',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2',
        address: '4500 Westpark Dr',
        eta: '10:45',
        window: '10:00 - 12:00',
        status: 'pending',
      },
      {
        id: 's3',
        address: '7200 Bellaire Blvd',
        eta: '12:10',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's4',
        address: '1800 Fondren Rd',
        eta: '13:30',
        window: '13:00 - 15:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c9',
        bol: 'BOL-91055',
        commodity: 'Electronics — Consumer Goods',
        weight: 6400,
        pallets: 8,
        stopId: 's1',
        status: 'delivered',
      },
      {
        id: 'c9b',
        bol: 'BOL-91056',
        commodity: 'Networking Equipment',
        weight: 4200,
        pallets: 5,
        stopId: 's2',
        status: 'in-transit',
      },
      {
        id: 'c9c',
        bol: 'BOL-91057',
        commodity: 'Display Monitors (Palletized)',
        weight: 3800,
        pallets: 6,
        stopId: 's3',
        status: 'loaded',
      },
      {
        id: 'c9d',
        bol: 'BOL-91058',
        commodity: 'Server Rack Components',
        weight: 5100,
        pallets: 4,
        stopId: 's4',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-james',
    name: 'James Wilson',
    truckId: 'T-1105',
    truckHeight: '13\'6"',
    status: 'warning',
    activityStatus: 'driving',
    hosDriven: 8.0,
    signalBars: 3,
    location: { x: 30, y: 40 },
    route: [
      {
        id: 's1',
        address: '1200 Airport Blvd',
        eta: '12:30',
        window: '12:00 - 14:00',
        status: 'completed',
      },
      {
        id: 's2',
        address: '3400 Gulf Fwy',
        eta: '14:10',
        window: '14:00 - 15:00',
        status: 'at-risk',
      },
      {
        id: 's3',
        address: '5600 Navigation Blvd',
        eta: '15:20',
        window: '15:00 - 17:00',
        status: 'pending',
      },
      {
        id: 's4',
        address: '8100 Harrisburg Blvd',
        eta: '16:40',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c10',
        bol: 'BOL-43780',
        commodity: 'Frozen Foods — Refrigerated',
        weight: 15600,
        pallets: 14,
        stopId: 's1',
        status: 'delivered',
      },
      {
        id: 'c10b',
        bol: 'BOL-43781',
        commodity: 'Dairy Products — Cold Chain',
        weight: 8400,
        pallets: 10,
        stopId: 's2',
        status: 'in-transit',
      },
      {
        id: 'c10c',
        bol: 'BOL-43782',
        commodity: 'Frozen Seafood',
        weight: 6200,
        pallets: 8,
        stopId: 's3',
        status: 'loaded',
      },
      {
        id: 'c10d',
        bol: 'BOL-43783',
        commodity: 'Ice Cream — Temp Sensitive',
        weight: 4800,
        pallets: 6,
        stopId: 's4',
        status: 'loaded',
      },
    ],
  },
]

const generatedDrivers: Driver[] = [
  {
    id: 'd-gen-0',
    name: 'Robert Garcia',
    truckId: 'T-4821',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 3.2,
    location: { x: 15, y: 25 },
    route: [
      {
        id: 's1-0',
        address: '4200 Memorial Dr',
        eta: '08:30',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-0',
        address: '1850 Westheimer Rd',
        eta: '10:15',
        window: '10:00 - 12:00',
        status: 'pending',
      },
      {
        id: 's3-0',
        address: '7300 Sam Houston Pkwy',
        eta: '12:30',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's4-0',
        address: '3500 Kirby Dr',
        eta: '14:45',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-0',
        bol: 'BOL-34210',
        commodity: 'Paper Products — Bulk',
        weight: 18200,
        pallets: 10,
        stopId: 's1-0',
        status: 'delivered',
      },
      {
        id: 'c2-0',
        bol: 'BOL-34211',
        commodity: 'Bottled Water (24-pack)',
        weight: 14600,
        pallets: 8,
        stopId: 's2-0',
        status: 'in-transit',
      },
      {
        id: 'c3-0',
        bol: 'BOL-34212',
        commodity: 'Canned Goods — Assorted',
        weight: 12400,
        pallets: 7,
        stopId: 's3-0',
        status: 'loaded',
      },
      {
        id: 'c4-0',
        bol: 'BOL-34213',
        commodity: 'Cleaning Supplies',
        weight: 9800,
        pallets: 5,
        stopId: 's4-0',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-1',
    name: 'Michael Williams',
    truckId: 'T-5932',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 5.8,
    location: { x: 72, y: 62 },
    route: [
      {
        id: 's1-1',
        address: '2900 Briarpark Dr',
        eta: '09:00',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-1',
        address: '5600 Richmond Ave',
        eta: '11:15',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's3-1',
        address: '8400 Westpark Dr',
        eta: '13:30',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-1',
        address: '1200 Dairy Ashford Rd',
        eta: '15:45',
        window: '15:00 - 17:00',
        status: 'pending',
      },
      {
        id: 's5-1',
        address: 'Return to Depot',
        eta: '17:30',
        window: 'N/A',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-1',
        bol: 'BOL-52180',
        commodity: 'Building Materials — Drywall',
        weight: 22400,
        pallets: 14,
        stopId: 's1-1',
        status: 'delivered',
      },
      {
        id: 'c2-1',
        bol: 'BOL-52181',
        commodity: 'Lumber — 2x4 Studs',
        weight: 19800,
        pallets: 12,
        stopId: 's2-1',
        status: 'delivered',
      },
      {
        id: 'c3-1',
        bol: 'BOL-52182',
        commodity: 'Roofing Shingles',
        weight: 16200,
        pallets: 10,
        stopId: 's3-1',
        status: 'in-transit',
      },
      {
        id: 'c4-1',
        bol: 'BOL-52183',
        commodity: 'Concrete Mix (80lb)',
        weight: 14400,
        pallets: 8,
        stopId: 's4-1',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-2',
    name: 'William Brown',
    truckId: 'T-3147',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'on-site',
    hosDriven: 1.4,
    location: { x: 38, y: 18 },
    route: [
      {
        id: 's1-2',
        address: '6700 Bellaire Blvd',
        eta: '09:30',
        window: '09:00 - 11:00',
        status: 'pending',
      },
      {
        id: 's2-2',
        address: '3200 Southwest Fwy',
        eta: '11:45',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-2',
        address: '9100 Hillcroft Ave',
        eta: '14:00',
        window: '13:00 - 15:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-2',
        bol: 'BOL-67340',
        commodity: 'Retail Merchandise — Apparel',
        weight: 12600,
        pallets: 6,
        stopId: 's1-2',
        status: 'in-transit',
      },
      {
        id: 'c2-2',
        bol: 'BOL-67341',
        commodity: 'Footwear — Boxed',
        weight: 10400,
        pallets: 5,
        stopId: 's2-2',
        status: 'loaded',
      },
      {
        id: 'c3-2',
        bol: 'BOL-67342',
        commodity: 'Accessories — Packaged',
        weight: 8200,
        pallets: 4,
        stopId: 's3-2',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-3',
    name: 'Joseph Martinez',
    truckId: 'T-7264',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 55, y: 44 },
    route: [
      {
        id: 's1-3',
        address: '1500 Eldridge Pkwy',
        eta: '08:00',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2-3',
        address: '4800 Beechnut St',
        eta: '10:30',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-3',
        address: '7600 Harwin Dr',
        eta: '13:00',
        window: '12:00 - 14:00',
        status: 'completed',
      },
      {
        id: 's4-3',
        address: '2200 Post Oak Blvd',
        eta: '15:15',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-3',
        bol: 'BOL-41920',
        commodity: 'Automotive Parts — Filters',
        weight: 28100,
        pallets: 18,
        stopId: 's1-3',
        status: 'delivered',
      },
      {
        id: 'c2-3',
        bol: 'BOL-41921',
        commodity: 'Motor Oil (Quarts)',
        weight: 24600,
        pallets: 15,
        stopId: 's2-3',
        status: 'delivered',
      },
      {
        id: 'c3-3',
        bol: 'BOL-41922',
        commodity: 'Brake Pads — Boxed',
        weight: 19800,
        pallets: 12,
        stopId: 's3-3',
        status: 'delivered',
      },
      {
        id: 'c4-3',
        bol: 'BOL-41923',
        commodity: 'Batteries — Automotive',
        weight: 16400,
        pallets: 10,
        stopId: 's4-3',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-4',
    name: 'Thomas Hernandez',
    truckId: 'T-1583',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 82, y: 75 },
    route: [
      {
        id: 's1-4',
        address: '3400 Montrose Blvd',
        eta: '07:30',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2-4',
        address: '5200 Woodway Dr',
        eta: '09:45',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3-4',
        address: '8800 Katy Fwy',
        eta: '12:00',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-4',
        address: 'Return to Depot',
        eta: '14:00',
        window: 'N/A',
        status: 'completed',
      },
    ],
    cargo: [
      {
        id: 'c1-4',
        bol: 'BOL-83450',
        commodity: 'Office Furniture — Desks',
        weight: 15800,
        pallets: 8,
        stopId: 's1-4',
        status: 'delivered',
      },
      {
        id: 'c2-4',
        bol: 'BOL-83451',
        commodity: 'Office Chairs (Boxed)',
        weight: 13200,
        pallets: 6,
        stopId: 's2-4',
        status: 'delivered',
      },
      {
        id: 'c3-4',
        bol: 'BOL-83452',
        commodity: 'Filing Cabinets',
        weight: 11600,
        pallets: 5,
        stopId: 's3-4',
        status: 'delivered',
      },
    ],
  },
  {
    id: 'd-gen-5',
    name: 'Charles Lopez',
    truckId: 'T-6418',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 4.5,
    location: { x: 22, y: 58 },
    route: [
      {
        id: 's1-5',
        address: '2700 Bissonnet St',
        eta: '09:15',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2-5',
        address: '6300 Hillcroft Ave',
        eta: '11:30',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-5',
        address: '4100 Westheimer Rd',
        eta: '13:45',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-5',
        address: '8500 Fondren Rd',
        eta: '16:00',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-5',
        bol: 'BOL-29870',
        commodity: 'Packaged Foods — Snacks',
        weight: 31200,
        pallets: 20,
        stopId: 's1-5',
        status: 'delivered',
      },
      {
        id: 'c2-5',
        bol: 'BOL-29871',
        commodity: 'Beverages — Canned',
        weight: 27800,
        pallets: 18,
        stopId: 's2-5',
        status: 'in-transit',
      },
      {
        id: 'c3-5',
        bol: 'BOL-29872',
        commodity: 'Cereal — Boxed',
        weight: 22400,
        pallets: 14,
        stopId: 's3-5',
        status: 'loaded',
      },
      {
        id: 'c4-5',
        bol: 'BOL-29873',
        commodity: 'Pasta Products',
        weight: 18600,
        pallets: 12,
        stopId: 's4-5',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-6',
    name: 'Christopher Gonzalez',
    truckId: 'T-8935',
    truckHeight: '12\'0"',
    status: 'normal',
    activityStatus: 'sleeper',
    hosDriven: 9.6,
    location: { x: 48, y: 32 },
    route: [
      {
        id: 's1-6',
        address: '5500 San Felipe St',
        eta: '10:00',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2-6',
        address: '3900 Voss Rd',
        eta: '12:15',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's3-6',
        address: '7200 Gessner Rd',
        eta: '14:30',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-6',
        bol: 'BOL-71560',
        commodity: 'Industrial Chemicals — Cleaning',
        weight: 9400,
        pallets: 4,
        stopId: 's1-6',
        status: 'delivered',
      },
      {
        id: 'c2-6',
        bol: 'BOL-71561',
        commodity: 'Solvents — Packaged',
        weight: 8200,
        pallets: 3,
        stopId: 's2-6',
        status: 'in-transit',
      },
      {
        id: 'c3-6',
        bol: 'BOL-71562',
        commodity: 'Lubricants — Drums',
        weight: 7600,
        pallets: 3,
        stopId: 's3-6',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-7',
    name: 'Daniel Wilson',
    truckId: 'T-2076',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'on-site',
    hosDriven: 6.3,
    location: { x: 68, y: 15 },
    route: [
      {
        id: 's1-7',
        address: '1800 West Loop S',
        eta: '08:45',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-7',
        address: '4500 Post Oak Pl',
        eta: '11:00',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-7',
        address: '6800 Westpark Dr',
        eta: '13:15',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-7',
        address: '9200 Bellaire Blvd',
        eta: '15:30',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-7',
        bol: 'BOL-45230',
        commodity: 'Textiles — Fabric Rolls',
        weight: 24600,
        pallets: 16,
        stopId: 's1-7',
        status: 'delivered',
      },
      {
        id: 'c2-7',
        bol: 'BOL-45231',
        commodity: 'Thread Spools (Bulk)',
        weight: 19800,
        pallets: 12,
        stopId: 's2-7',
        status: 'delivered',
      },
      {
        id: 'c3-7',
        bol: 'BOL-45232',
        commodity: 'Buttons & Fasteners',
        weight: 14200,
        pallets: 8,
        stopId: 's3-7',
        status: 'in-transit',
      },
      {
        id: 'c4-7',
        bol: 'BOL-45233',
        commodity: 'Zippers — Packaged',
        weight: 11400,
        pallets: 6,
        stopId: 's4-7',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-8',
    name: 'Matthew Anderson',
    truckId: 'T-3891',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 8.4,
    location: { x: 35, y: 72 },
    route: [
      {
        id: 's1-8',
        address: '2100 Travis St',
        eta: '07:00',
        window: '06:00 - 08:00',
        status: 'completed',
      },
      {
        id: 's2-8',
        address: '5400 Fannin St',
        eta: '09:30',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3-8',
        address: '8100 Cambridge St',
        eta: '12:00',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-8',
        address: '3700 Main St',
        eta: '14:15',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's5-8',
        address: 'Return to Depot',
        eta: '16:30',
        window: 'N/A',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-8',
        bol: 'BOL-58940',
        commodity: 'Plumbing Fixtures — Faucets',
        weight: 19800,
        pallets: 12,
        stopId: 's1-8',
        status: 'delivered',
      },
      {
        id: 'c2-8',
        bol: 'BOL-58941',
        commodity: 'PVC Pipe (10ft sections)',
        weight: 17200,
        pallets: 10,
        stopId: 's2-8',
        status: 'delivered',
      },
      {
        id: 'c3-8',
        bol: 'BOL-58942',
        commodity: 'Toilet Tanks & Bowls',
        weight: 14600,
        pallets: 8,
        stopId: 's3-8',
        status: 'delivered',
      },
      {
        id: 'c4-8',
        bol: 'BOL-58943',
        commodity: 'Copper Fittings',
        weight: 11800,
        pallets: 6,
        stopId: 's4-8',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-9',
    name: 'Anthony Thomas',
    truckId: 'T-5247',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 58, y: 88 },
    route: [
      {
        id: 's1-9',
        address: '4200 Airline Dr',
        eta: '10:30',
        window: '10:00 - 12:00',
        status: 'pending',
      },
      {
        id: 's2-9',
        address: '7800 North Fwy',
        eta: '12:45',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's3-9',
        address: '2500 Greens Rd',
        eta: '15:00',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's4-9',
        address: '9600 Veterans Memorial Dr',
        eta: '17:15',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-9',
        bol: 'BOL-36780',
        commodity: 'Electrical Wire (Spools)',
        weight: 14200,
        pallets: 7,
        stopId: 's1-9',
        status: 'in-transit',
      },
      {
        id: 'c2-9',
        bol: 'BOL-36781',
        commodity: 'Light Fixtures — LED',
        weight: 12600,
        pallets: 6,
        stopId: 's2-9',
        status: 'loaded',
      },
      {
        id: 'c3-9',
        bol: 'BOL-36782',
        commodity: 'Circuit Breakers',
        weight: 10800,
        pallets: 5,
        stopId: 's3-9',
        status: 'loaded',
      },
      {
        id: 'c4-9',
        bol: 'BOL-36783',
        commodity: 'Conduit & Boxes',
        weight: 9200,
        pallets: 4,
        stopId: 's4-9',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-10',
    name: 'Mark Taylor',
    truckId: 'T-4163',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 12, y: 42 },
    route: [
      {
        id: 's1-10',
        address: '3300 Chimney Rock Rd',
        eta: '06:30',
        window: '06:00 - 08:00',
        status: 'completed',
      },
      {
        id: 's2-10',
        address: '6700 Renwick Dr',
        eta: '09:00',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's3-10',
        address: '8900 Westheimer Rd',
        eta: '11:30',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-10',
        address: '1700 Bering Dr',
        eta: '13:45',
        window: '13:00 - 15:00',
        status: 'completed',
      },
      {
        id: 's5-10',
        address: 'Return to Depot',
        eta: '15:30',
        window: 'N/A',
        status: 'completed',
      },
    ],
    cargo: [
      {
        id: 'c1-10',
        bol: 'BOL-62150',
        commodity: 'Paint — Interior (5gal)',
        weight: 27300,
        pallets: 15,
        stopId: 's1-10',
        status: 'delivered',
      },
      {
        id: 'c2-10',
        bol: 'BOL-62151',
        commodity: 'Paint Brushes & Rollers',
        weight: 22800,
        pallets: 12,
        stopId: 's2-10',
        status: 'delivered',
      },
      {
        id: 'c3-10',
        bol: 'BOL-62152',
        commodity: 'Primer — White (5gal)',
        weight: 18400,
        pallets: 10,
        stopId: 's3-10',
        status: 'delivered',
      },
      {
        id: 'c4-10',
        bol: 'BOL-62153',
        commodity: 'Caulk & Sealants',
        weight: 14200,
        pallets: 8,
        stopId: 's4-10',
        status: 'delivered',
      },
    ],
  },
  {
    id: 'd-gen-11',
    name: 'Donald Moore',
    truckId: 'T-7528',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 3.7,
    location: { x: 75, y: 38 },
    route: [
      {
        id: 's1-11',
        address: '4900 Woodway Dr',
        eta: '09:30',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2-11',
        address: '7300 Stella Link Rd',
        eta: '11:45',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-11',
        address: '2800 Kirby Dr',
        eta: '14:00',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-11',
        address: '5600 Almeda Rd',
        eta: '16:15',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-11',
        bol: 'BOL-19430',
        commodity: 'Hardware — Nuts & Bolts',
        weight: 33400,
        pallets: 22,
        stopId: 's1-11',
        status: 'delivered',
      },
      {
        id: 'c2-11',
        bol: 'BOL-19431',
        commodity: 'Power Tools (Boxed)',
        weight: 28600,
        pallets: 18,
        stopId: 's2-11',
        status: 'in-transit',
      },
      {
        id: 'c3-11',
        bol: 'BOL-19432',
        commodity: 'Hand Tools — Assorted',
        weight: 24200,
        pallets: 14,
        stopId: 's3-11',
        status: 'loaded',
      },
      {
        id: 'c4-11',
        bol: 'BOL-19433',
        commodity: 'Safety Equipment',
        weight: 19800,
        pallets: 11,
        stopId: 's4-11',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-12',
    name: 'Steven Jackson',
    truckId: 'T-1342',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 5.1,
    location: { x: 42, y: 55 },
    route: [
      {
        id: 's1-12',
        address: '3100 Southwest Fwy',
        eta: '08:15',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-12',
        address: '6400 Hillcroft Ave',
        eta: '10:30',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-12',
        address: '8700 Beechnut St',
        eta: '12:45',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's4-12',
        address: '1900 Fondren Rd',
        eta: '15:00',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-12',
        bol: 'BOL-84620',
        commodity: 'Sporting Goods — Equipment',
        weight: 11800,
        pallets: 5,
        stopId: 's1-12',
        status: 'delivered',
      },
      {
        id: 'c2-12',
        bol: 'BOL-84621',
        commodity: 'Athletic Apparel',
        weight: 10200,
        pallets: 4,
        stopId: 's2-12',
        status: 'delivered',
      },
      {
        id: 'c3-12',
        bol: 'BOL-84622',
        commodity: 'Fitness Accessories',
        weight: 8600,
        pallets: 3,
        stopId: 's3-12',
        status: 'in-transit',
      },
      {
        id: 'c4-12',
        bol: 'BOL-84623',
        commodity: 'Outdoor Gear',
        weight: 7400,
        pallets: 3,
        stopId: 's4-12',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-13',
    name: 'Paul Martin',
    truckId: 'T-6789',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'on-site',
    hosDriven: 0.8,
    location: { x: 88, y: 48 },
    route: [
      {
        id: 's1-13',
        address: '5100 Westheimer Rd',
        eta: '11:00',
        window: '10:00 - 12:00',
        status: 'pending',
      },
      {
        id: 's2-13',
        address: '8300 Richmond Ave',
        eta: '13:15',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's3-13',
        address: '2400 Sage Rd',
        eta: '15:30',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-13',
        bol: 'BOL-53870',
        commodity: 'Pet Supplies — Dog Food',
        weight: 20600,
        pallets: 11,
        stopId: 's1-13',
        status: 'in-transit',
      },
      {
        id: 'c2-13',
        bol: 'BOL-53871',
        commodity: 'Cat Litter (Bags)',
        weight: 17800,
        pallets: 9,
        stopId: 's2-13',
        status: 'loaded',
      },
      {
        id: 'c3-13',
        bol: 'BOL-53872',
        commodity: 'Pet Toys & Accessories',
        weight: 14200,
        pallets: 7,
        stopId: 's3-13',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-14',
    name: 'Andrew Lee',
    truckId: 'T-2456',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 7.6,
    location: { x: 28, y: 82 },
    route: [
      {
        id: 's1-14',
        address: '1400 Dairy Ashford Rd',
        eta: '07:45',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2-14',
        address: '4700 Eldridge Pkwy',
        eta: '10:00',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3-14',
        address: '7900 Westheimer Rd',
        eta: '12:15',
        window: '12:00 - 14:00',
        status: 'completed',
      },
      {
        id: 's4-14',
        address: '3200 Wilcrest Dr',
        eta: '14:30',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-14',
        bol: 'BOL-47290',
        commodity: 'Garden Supplies — Soil',
        weight: 16400,
        pallets: 9,
        stopId: 's1-14',
        status: 'delivered',
      },
      {
        id: 'c2-14',
        bol: 'BOL-47291',
        commodity: 'Fertilizer (Bags)',
        weight: 14800,
        pallets: 8,
        stopId: 's2-14',
        status: 'delivered',
      },
      {
        id: 'c3-14',
        bol: 'BOL-47292',
        commodity: 'Plant Seeds — Bulk',
        weight: 12200,
        pallets: 6,
        stopId: 's3-14',
        status: 'delivered',
      },
      {
        id: 'c4-14',
        bol: 'BOL-47293',
        commodity: 'Garden Tools',
        weight: 10600,
        pallets: 5,
        stopId: 's4-14',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-15',
    name: 'Joshua Perez',
    truckId: 'T-8174',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 62, y: 22 },
    route: [
      {
        id: 's1-15',
        address: '2600 Navigation Blvd',
        eta: '09:00',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-15',
        address: '5800 Clinton Dr',
        eta: '11:15',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-15',
        address: '8200 Harrisburg Blvd',
        eta: '13:30',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-15',
        address: '3900 Lockwood Dr',
        eta: '15:45',
        window: '15:00 - 17:00',
        status: 'pending',
      },
      {
        id: 's5-15',
        address: 'Return to Depot',
        eta: '17:30',
        window: 'N/A',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-15',
        bol: 'BOL-75410',
        commodity: 'Bakery Supplies — Flour',
        weight: 25800,
        pallets: 17,
        stopId: 's1-15',
        status: 'delivered',
      },
      {
        id: 'c2-15',
        bol: 'BOL-75411',
        commodity: 'Sugar (50lb bags)',
        weight: 22400,
        pallets: 14,
        stopId: 's2-15',
        status: 'in-transit',
      },
      {
        id: 'c3-15',
        bol: 'BOL-75412',
        commodity: 'Baking Mixes',
        weight: 18600,
        pallets: 11,
        stopId: 's3-15',
        status: 'loaded',
      },
      {
        id: 'c4-15',
        bol: 'BOL-75413',
        commodity: 'Yeast & Leavening',
        weight: 14800,
        pallets: 8,
        stopId: 's4-15',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-16',
    name: 'Kenneth Thompson',
    truckId: 'T-3695',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 18, y: 65 },
    route: [
      {
        id: 's1-16',
        address: '4100 Montrose Blvd',
        eta: '06:00',
        window: '05:00 - 07:00',
        status: 'completed',
      },
      {
        id: 's2-16',
        address: '6900 Main St',
        eta: '08:30',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's3-16',
        address: '9400 Fannin St',
        eta: '11:00',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's4-16',
        address: 'Return to Depot',
        eta: '13:00',
        window: 'N/A',
        status: 'completed',
      },
    ],
    cargo: [
      {
        id: 'c1-16',
        bol: 'BOL-31680',
        commodity: 'Medical Supplies — Bandages',
        weight: 8600,
        pallets: 3,
        stopId: 's1-16',
        status: 'delivered',
      },
      {
        id: 'c2-16',
        bol: 'BOL-31681',
        commodity: 'Surgical Gloves (Boxes)',
        weight: 7400,
        pallets: 3,
        stopId: 's2-16',
        status: 'delivered',
      },
      {
        id: 'c3-16',
        bol: 'BOL-31682',
        commodity: 'Syringes — Sterile',
        weight: 6200,
        pallets: 2,
        stopId: 's3-16',
        status: 'delivered',
      },
    ],
  },
  {
    id: 'd-gen-17',
    name: 'Kevin White',
    truckId: 'T-5013',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 2.5,
    location: { x: 52, y: 78 },
    route: [
      {
        id: 's1-17',
        address: '3500 Memorial Dr',
        eta: '10:15',
        window: '10:00 - 12:00',
        status: 'pending',
      },
      {
        id: 's2-17',
        address: '6200 Washington Ave',
        eta: '12:30',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's3-17',
        address: '8800 Katy Fwy',
        eta: '14:45',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's4-17',
        address: '2100 Durham Dr',
        eta: '17:00',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-17',
        bol: 'BOL-68530',
        commodity: 'Toys — Action Figures',
        weight: 29400,
        pallets: 19,
        stopId: 's1-17',
        status: 'in-transit',
      },
      {
        id: 'c2-17',
        bol: 'BOL-68531',
        commodity: 'Board Games (Boxed)',
        weight: 24600,
        pallets: 15,
        stopId: 's2-17',
        status: 'loaded',
      },
      {
        id: 'c3-17',
        bol: 'BOL-68532',
        commodity: 'Puzzles — Assorted',
        weight: 19800,
        pallets: 12,
        stopId: 's3-17',
        status: 'loaded',
      },
      {
        id: 'c4-17',
        bol: 'BOL-68533',
        commodity: 'Craft Supplies',
        weight: 16200,
        pallets: 9,
        stopId: 's4-17',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-18',
    name: 'Brian Harris',
    truckId: 'T-9247',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 6.9,
    location: { x: 78, y: 52 },
    route: [
      {
        id: 's1-18',
        address: '1900 West Loop S',
        eta: '08:00',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2-18',
        address: '4600 Post Oak Pl',
        eta: '10:15',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-18',
        address: '7200 San Felipe St',
        eta: '12:30',
        window: '12:00 - 14:00',
        status: 'completed',
      },
      {
        id: 's4-18',
        address: '9800 Westheimer Rd',
        eta: '14:45',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-18',
        bol: 'BOL-42760',
        commodity: 'Books — Hardcover',
        weight: 13200,
        pallets: 6,
        stopId: 's1-18',
        status: 'delivered',
      },
      {
        id: 'c2-18',
        bol: 'BOL-42761',
        commodity: 'Magazines — Bundled',
        weight: 11600,
        pallets: 5,
        stopId: 's2-18',
        status: 'delivered',
      },
      {
        id: 'c3-18',
        bol: 'BOL-42762',
        commodity: 'Stationery Supplies',
        weight: 9800,
        pallets: 4,
        stopId: 's3-18',
        status: 'delivered',
      },
      {
        id: 'c4-18',
        bol: 'BOL-42763',
        commodity: 'Greeting Cards',
        weight: 8200,
        pallets: 3,
        stopId: 's4-18',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-19',
    name: 'George Sanchez',
    truckId: 'T-1628',
    truckHeight: '12\'0"',
    status: 'normal',
    activityStatus: 'on-site',
    hosDriven: 1.1,
    location: { x: 32, y: 35 },
    route: [
      {
        id: 's1-19',
        address: '5300 Bissonnet St',
        eta: '11:30',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's2-19',
        address: '8100 Bellaire Blvd',
        eta: '13:45',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's3-19',
        address: '3700 Chimney Rock Rd',
        eta: '16:00',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-19',
        bol: 'BOL-56940',
        commodity: 'Cosmetics — Skincare',
        weight: 21800,
        pallets: 13,
        stopId: 's1-19',
        status: 'in-transit',
      },
      {
        id: 'c2-19',
        bol: 'BOL-56941',
        commodity: 'Hair Care Products',
        weight: 18400,
        pallets: 10,
        stopId: 's2-19',
        status: 'loaded',
      },
      {
        id: 'c3-19',
        bol: 'BOL-56942',
        commodity: 'Fragrances — Boxed',
        weight: 14600,
        pallets: 8,
        stopId: 's3-19',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-20',
    name: 'Edward Clark',
    truckId: 'T-7381',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 8.7,
    location: { x: 45, y: 12 },
    route: [
      {
        id: 's1-20',
        address: '2200 Westheimer Rd',
        eta: '06:45',
        window: '06:00 - 08:00',
        status: 'completed',
      },
      {
        id: 's2-20',
        address: '5700 Richmond Ave',
        eta: '09:00',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's3-20',
        address: '8300 Hillcroft Ave',
        eta: '11:15',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-20',
        address: '4100 Fondren Rd',
        eta: '13:30',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's5-20',
        address: 'Return to Depot',
        eta: '15:45',
        window: 'N/A',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-20',
        bol: 'BOL-23150',
        commodity: 'Household Cleaners',
        weight: 17600,
        pallets: 10,
        stopId: 's1-20',
        status: 'delivered',
      },
      {
        id: 'c2-20',
        bol: 'BOL-23151',
        commodity: 'Laundry Detergent',
        weight: 15800,
        pallets: 9,
        stopId: 's2-20',
        status: 'delivered',
      },
      {
        id: 'c3-20',
        bol: 'BOL-23152',
        commodity: 'Dish Soap (Bottles)',
        weight: 13200,
        pallets: 7,
        stopId: 's3-20',
        status: 'delivered',
      },
      {
        id: 'c4-20',
        bol: 'BOL-23153',
        commodity: 'Air Fresheners',
        weight: 10600,
        pallets: 5,
        stopId: 's4-20',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-21',
    name: 'Ronald Ramirez',
    truckId: 'T-4592',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'sleeper',
    hosDriven: 10.2,
    location: { x: 85, y: 30 },
    route: [
      {
        id: 's1-21',
        address: '3800 Woodway Dr',
        eta: '09:45',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2-21',
        address: '6500 Voss Rd',
        eta: '12:00',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-21',
        address: '9100 Gessner Rd',
        eta: '14:15',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's4-21',
        address: '2700 Beltway 8',
        eta: '16:30',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-21',
        bol: 'BOL-79380',
        commodity: 'Furniture — Sofas',
        weight: 26200,
        pallets: 16,
        stopId: 's1-21',
        status: 'delivered',
      },
      {
        id: 'c2-21',
        bol: 'BOL-79381',
        commodity: 'Dining Tables (Boxed)',
        weight: 22800,
        pallets: 13,
        stopId: 's2-21',
        status: 'in-transit',
      },
      {
        id: 'c3-21',
        bol: 'BOL-79382',
        commodity: 'Bed Frames',
        weight: 18600,
        pallets: 10,
        stopId: 's3-21',
        status: 'loaded',
      },
      {
        id: 'c4-21',
        bol: 'BOL-79383',
        commodity: 'Mattresses — Wrapped',
        weight: 15400,
        pallets: 8,
        stopId: 's4-21',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-22',
    name: 'Timothy Lewis',
    truckId: 'T-2834',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 5.6,
    location: { x: 20, y: 48 },
    route: [
      {
        id: 's1-22',
        address: '4300 Airline Dr',
        eta: '08:30',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-22',
        address: '7100 North Fwy',
        eta: '10:45',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-22',
        address: '9600 Greens Rd',
        eta: '13:00',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's4-22',
        address: '2800 Aldine Mail Rt',
        eta: '15:15',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-22',
        bol: 'BOL-14870',
        commodity: 'Tires — Automotive',
        weight: 10200,
        pallets: 4,
        stopId: 's1-22',
        status: 'delivered',
      },
      {
        id: 'c2-22',
        bol: 'BOL-14871',
        commodity: 'Wheels & Rims',
        weight: 9400,
        pallets: 4,
        stopId: 's2-22',
        status: 'delivered',
      },
      {
        id: 'c3-22',
        bol: 'BOL-14872',
        commodity: 'Tire Accessories',
        weight: 7800,
        pallets: 3,
        stopId: 's3-22',
        status: 'in-transit',
      },
      {
        id: 'c4-22',
        bol: 'BOL-14873',
        commodity: 'Wheel Weights',
        weight: 6200,
        pallets: 2,
        stopId: 's4-22',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-23',
    name: 'Jason Robinson',
    truckId: 'T-6157',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 5.1,
    location: { x: 65, y: 68 },
    route: [
      {
        id: 's1-23',
        address: '3600 Travis St',
        eta: '07:15',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2-23',
        address: '6200 Main St',
        eta: '09:30',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3-23',
        address: '8900 Fannin St',
        eta: '11:45',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-23',
        address: '4700 Almeda Rd',
        eta: '14:00',
        window: '13:00 - 15:00',
        status: 'completed',
      },
      {
        id: 's5-23',
        address: 'Return to Depot',
        eta: '15:45',
        window: 'N/A',
        status: 'completed',
      },
    ],
    cargo: [
      {
        id: 'c1-23',
        bol: 'BOL-65240',
        commodity: 'Window Treatments — Blinds',
        weight: 23400,
        pallets: 14,
        stopId: 's1-23',
        status: 'delivered',
      },
      {
        id: 'c2-23',
        bol: 'BOL-65241',
        commodity: 'Curtain Rods',
        weight: 19800,
        pallets: 11,
        stopId: 's2-23',
        status: 'delivered',
      },
      {
        id: 'c3-23',
        bol: 'BOL-65242',
        commodity: 'Drapes — Packaged',
        weight: 16200,
        pallets: 9,
        stopId: 's3-23',
        status: 'delivered',
      },
      {
        id: 'c4-23',
        bol: 'BOL-65243',
        commodity: 'Shades — Roller',
        weight: 13600,
        pallets: 7,
        stopId: 's4-23',
        status: 'delivered',
      },
    ],
  },
  {
    id: 'd-gen-24',
    name: 'Jeffrey Smith',
    truckId: 'T-8463',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 40, y: 85 },
    route: [
      {
        id: 's1-24',
        address: '5400 Westpark Dr',
        eta: '12:00',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's2-24',
        address: '8200 Richmond Ave',
        eta: '14:15',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's3-24',
        address: '3100 Sage Rd',
        eta: '16:30',
        window: '16:00 - 18:00',
        status: 'pending',
      },
      {
        id: 's4-24',
        address: '6700 Westheimer Rd',
        eta: '18:45',
        window: '18:00 - 20:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-24',
        bol: 'BOL-38620',
        commodity: 'Flooring — Laminate',
        weight: 30800,
        pallets: 21,
        stopId: 's1-24',
        status: 'in-transit',
      },
      {
        id: 'c2-24',
        bol: 'BOL-38621',
        commodity: 'Carpet Tiles',
        weight: 26400,
        pallets: 17,
        stopId: 's2-24',
        status: 'loaded',
      },
      {
        id: 'c3-24',
        bol: 'BOL-38622',
        commodity: 'Vinyl Planks',
        weight: 22800,
        pallets: 14,
        stopId: 's3-24',
        status: 'loaded',
      },
      {
        id: 'c4-24',
        bol: 'BOL-38623',
        commodity: 'Underlayment',
        weight: 18200,
        pallets: 11,
        stopId: 's4-24',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-25',
    name: 'Ryan Johnson',
    truckId: 'T-1975',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 7.3,
    location: { x: 55, y: 40 },
    route: [
      {
        id: 's1-25',
        address: '2900 Kirby Dr',
        eta: '07:30',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's2-25',
        address: '5600 Stella Link Rd',
        eta: '09:45',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3-25',
        address: '8200 Almeda Rd',
        eta: '12:00',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-25',
        address: '3800 OST',
        eta: '14:15',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-25',
        bol: 'BOL-82190',
        commodity: 'Kitchen Cabinets',
        weight: 15200,
        pallets: 8,
        stopId: 's1-25',
        status: 'delivered',
      },
      {
        id: 'c2-25',
        bol: 'BOL-82191',
        commodity: 'Countertops — Granite',
        weight: 13800,
        pallets: 7,
        stopId: 's2-25',
        status: 'delivered',
      },
      {
        id: 'c3-25',
        bol: 'BOL-82192',
        commodity: 'Sinks — Stainless',
        weight: 11400,
        pallets: 6,
        stopId: 's3-25',
        status: 'delivered',
      },
      {
        id: 'c4-25',
        bol: 'BOL-82193',
        commodity: 'Cabinet Hardware',
        weight: 9200,
        pallets: 4,
        stopId: 's4-25',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-26',
    name: 'Jacob Jones',
    truckId: 'T-3248',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'on-site',
    hosDriven: 2.0,
    location: { x: 25, y: 15 },
    route: [
      {
        id: 's1-26',
        address: '4800 Memorial Dr',
        eta: '10:45',
        window: '10:00 - 12:00',
        status: 'pending',
      },
      {
        id: 's2-26',
        address: '7300 Washington Ave',
        eta: '13:00',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's3-26',
        address: '2100 Durham Dr',
        eta: '15:15',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-26',
        bol: 'BOL-49570',
        commodity: 'Appliances — Refrigerators',
        weight: 28600,
        pallets: 18,
        stopId: 's1-26',
        status: 'in-transit',
      },
      {
        id: 'c2-26',
        bol: 'BOL-49571',
        commodity: 'Dishwashers (Boxed)',
        weight: 24200,
        pallets: 14,
        stopId: 's2-26',
        status: 'loaded',
      },
      {
        id: 'c3-26',
        bol: 'BOL-49572',
        commodity: 'Microwaves',
        weight: 19800,
        pallets: 11,
        stopId: 's3-26',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-27',
    name: 'Gary Miller',
    truckId: 'T-7619',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 70, y: 55 },
    route: [
      {
        id: 's1-27',
        address: '3200 Post Oak Blvd',
        eta: '09:00',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-27',
        address: '6800 San Felipe St',
        eta: '11:15',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-27',
        address: '9400 Westheimer Rd',
        eta: '13:30',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-27',
        address: '4600 Voss Rd',
        eta: '15:45',
        window: '15:00 - 17:00',
        status: 'pending',
      },
      {
        id: 's5-27',
        address: 'Return to Depot',
        eta: '17:30',
        window: 'N/A',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-27',
        bol: 'BOL-57830',
        commodity: 'HVAC Units — Residential',
        weight: 11400,
        pallets: 5,
        stopId: 's1-27',
        status: 'delivered',
      },
      {
        id: 'c2-27',
        bol: 'BOL-57831',
        commodity: 'Air Filters (Bulk)',
        weight: 10200,
        pallets: 4,
        stopId: 's2-27',
        status: 'in-transit',
      },
      {
        id: 'c3-27',
        bol: 'BOL-57832',
        commodity: 'Ductwork — Flexible',
        weight: 8600,
        pallets: 3,
        stopId: 's3-27',
        status: 'loaded',
      },
      {
        id: 'c4-27',
        bol: 'BOL-57833',
        commodity: 'Thermostats',
        weight: 7200,
        pallets: 3,
        stopId: 's4-27',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-28',
    name: 'Nicholas Davis',
    truckId: 'T-5486',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 6.1,
    location: { x: 48, y: 72 },
    route: [
      {
        id: 's1-28',
        address: '2400 Southwest Fwy',
        eta: '08:15',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-28',
        address: '5900 Hillcroft Ave',
        eta: '10:30',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-28',
        address: '8500 Beechnut St',
        eta: '12:45',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's4-28',
        address: '3700 Fondren Rd',
        eta: '15:00',
        window: '14:00 - 16:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-28',
        bol: 'BOL-73460',
        commodity: 'Lighting — LED Bulbs',
        weight: 19200,
        pallets: 11,
        stopId: 's1-28',
        status: 'delivered',
      },
      {
        id: 'c2-28',
        bol: 'BOL-73461',
        commodity: 'Ceiling Fans',
        weight: 16800,
        pallets: 9,
        stopId: 's2-28',
        status: 'delivered',
      },
      {
        id: 'c3-28',
        bol: 'BOL-73462',
        commodity: 'Lamp Fixtures',
        weight: 13400,
        pallets: 7,
        stopId: 's3-28',
        status: 'in-transit',
      },
      {
        id: 'c4-28',
        bol: 'BOL-73463',
        commodity: 'Light Switches',
        weight: 10600,
        pallets: 5,
        stopId: 's4-28',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-29',
    name: 'Eric Rodriguez',
    truckId: 'T-2193',
    truckHeight: '12\'0"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 8.9,
    location: { x: 15, y: 78 },
    route: [
      {
        id: 's1-29',
        address: '1600 Dairy Ashford Rd',
        eta: '06:30',
        window: '06:00 - 08:00',
        status: 'completed',
      },
      {
        id: 's2-29',
        address: '4900 Eldridge Pkwy',
        eta: '08:45',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's3-29',
        address: '7600 Westheimer Rd',
        eta: '11:00',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's4-29',
        address: '3400 Wilcrest Dr',
        eta: '13:15',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's5-29',
        address: 'Return to Depot',
        eta: '15:30',
        window: 'N/A',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-29',
        bol: 'BOL-26740',
        commodity: 'Insulation — Fiberglass',
        weight: 34200,
        pallets: 23,
        stopId: 's1-29',
        status: 'delivered',
      },
      {
        id: 'c2-29',
        bol: 'BOL-26741',
        commodity: 'Foam Boards',
        weight: 28600,
        pallets: 18,
        stopId: 's2-29',
        status: 'delivered',
      },
      {
        id: 'c3-29',
        bol: 'BOL-26742',
        commodity: 'Vapor Barriers',
        weight: 24200,
        pallets: 15,
        stopId: 's3-29',
        status: 'delivered',
      },
      {
        id: 'c4-29',
        bol: 'BOL-26743',
        commodity: 'Weatherstripping',
        weight: 19800,
        pallets: 12,
        stopId: 's4-29',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-30',
    name: 'Jonathan Garcia',
    truckId: 'T-8752',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 4.2,
    location: { x: 60, y: 28 },
    route: [
      {
        id: 's1-30',
        address: '4500 Montrose Blvd',
        eta: '05:45',
        window: '05:00 - 07:00',
        status: 'completed',
      },
      {
        id: 's2-30',
        address: '7100 Main St',
        eta: '08:00',
        window: '07:00 - 09:00',
        status: 'completed',
      },
      {
        id: 's3-30',
        address: '9700 Fannin St',
        eta: '10:15',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's4-30',
        address: 'Return to Depot',
        eta: '12:30',
        window: 'N/A',
        status: 'completed',
      },
    ],
    cargo: [
      {
        id: 'c1-30',
        bol: 'BOL-91350',
        commodity: 'Bedding — Sheets',
        weight: 7800,
        pallets: 3,
        stopId: 's1-30',
        status: 'delivered',
      },
      {
        id: 'c2-30',
        bol: 'BOL-91351',
        commodity: 'Pillows (Packaged)',
        weight: 6800,
        pallets: 3,
        stopId: 's2-30',
        status: 'delivered',
      },
      {
        id: 'c3-30',
        bol: 'BOL-91352',
        commodity: 'Comforters',
        weight: 5600,
        pallets: 2,
        stopId: 's3-30',
        status: 'delivered',
      },
    ],
  },
  {
    id: 'd-gen-31',
    name: 'Stephen Martinez',
    truckId: 'T-4037',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 1.6,
    location: { x: 82, y: 62 },
    route: [
      {
        id: 's1-31',
        address: '3700 West Loop S',
        eta: '11:15',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's2-31',
        address: '6300 Post Oak Pl',
        eta: '13:30',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's3-31',
        address: '8900 Westpark Dr',
        eta: '15:45',
        window: '15:00 - 17:00',
        status: 'pending',
      },
      {
        id: 's4-31',
        address: '2500 Bellaire Blvd',
        eta: '18:00',
        window: '17:00 - 19:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-31',
        bol: 'BOL-44680',
        commodity: 'Bathroom Fixtures — Vanities',
        weight: 22800,
        pallets: 13,
        stopId: 's1-31',
        status: 'in-transit',
      },
      {
        id: 'c2-31',
        bol: 'BOL-44681',
        commodity: 'Shower Doors',
        weight: 19400,
        pallets: 10,
        stopId: 's2-31',
        status: 'loaded',
      },
      {
        id: 'c3-31',
        bol: 'BOL-44682',
        commodity: 'Bathtubs — Acrylic',
        weight: 16200,
        pallets: 8,
        stopId: 's3-31',
        status: 'loaded',
      },
      {
        id: 'c4-31',
        bol: 'BOL-44683',
        commodity: 'Bathroom Mirrors',
        weight: 13600,
        pallets: 6,
        stopId: 's4-31',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-32',
    name: 'Larry Hernandez',
    truckId: 'T-6324',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 3.9,
    location: { x: 38, y: 45 },
    route: [
      {
        id: 's1-32',
        address: '2700 Bissonnet St',
        eta: '09:30',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2-32',
        address: '5400 Hillcroft Ave',
        eta: '11:45',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-32',
        address: '8100 Beechnut St',
        eta: '14:00',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-32',
        address: '3900 Fondren Rd',
        eta: '16:15',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-32',
        bol: 'BOL-18920',
        commodity: 'Doors — Interior',
        weight: 25400,
        pallets: 15,
        stopId: 's1-32',
        status: 'delivered',
      },
      {
        id: 'c2-32',
        bol: 'BOL-18921',
        commodity: 'Door Hardware — Knobs',
        weight: 21800,
        pallets: 12,
        stopId: 's2-32',
        status: 'in-transit',
      },
      {
        id: 'c3-32',
        bol: 'BOL-18922',
        commodity: 'Hinges — Bulk',
        weight: 18200,
        pallets: 10,
        stopId: 's3-32',
        status: 'loaded',
      },
      {
        id: 'c4-32',
        bol: 'BOL-18923',
        commodity: 'Door Frames',
        weight: 14600,
        pallets: 8,
        stopId: 's4-32',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-33',
    name: 'Justin Lopez',
    truckId: 'T-1856',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'on-site',
    hosDriven: 5.3,
    location: { x: 52, y: 18 },
    route: [
      {
        id: 's1-33',
        address: '4200 West Loop S',
        eta: '08:45',
        window: '08:00 - 10:00',
        status: 'completed',
      },
      {
        id: 's2-33',
        address: '6900 Post Oak Pl',
        eta: '11:00',
        window: '10:00 - 12:00',
        status: 'completed',
      },
      {
        id: 's3-33',
        address: '9500 San Felipe St',
        eta: '13:15',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-33',
        address: '3100 Westheimer Rd',
        eta: '15:30',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-33',
        bol: 'BOL-63570',
        commodity: 'Windows — Double-Hung',
        weight: 16800,
        pallets: 9,
        stopId: 's1-33',
        status: 'delivered',
      },
      {
        id: 'c2-33',
        bol: 'BOL-63571',
        commodity: 'Window Screens',
        weight: 14200,
        pallets: 7,
        stopId: 's2-33',
        status: 'delivered',
      },
      {
        id: 'c3-33',
        bol: 'BOL-63572',
        commodity: 'Sliding Glass Doors',
        weight: 11800,
        pallets: 6,
        stopId: 's3-33',
        status: 'in-transit',
      },
      {
        id: 'c4-33',
        bol: 'BOL-63573',
        commodity: 'Storm Windows',
        weight: 9400,
        pallets: 4,
        stopId: 's4-33',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-34',
    name: 'Scott Gonzalez',
    truckId: 'T-9541',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 7.8,
    location: { x: 75, y: 82 },
    route: [
      {
        id: 's1-34',
        address: '2100 Navigation Blvd',
        eta: '07:00',
        window: '06:00 - 08:00',
        status: 'completed',
      },
      {
        id: 's2-34',
        address: '5300 Clinton Dr',
        eta: '09:15',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's3-34',
        address: '7900 Harrisburg Blvd',
        eta: '11:30',
        window: '11:00 - 13:00',
        status: 'completed',
      },
      {
        id: 's4-34',
        address: '4200 Lockwood Dr',
        eta: '13:45',
        window: '13:00 - 15:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-34',
        bol: 'BOL-37290',
        commodity: 'Roofing Materials — Tar Paper',
        weight: 20200,
        pallets: 12,
        stopId: 's1-34',
        status: 'delivered',
      },
      {
        id: 'c2-34',
        bol: 'BOL-37291',
        commodity: 'Flashing — Metal',
        weight: 17600,
        pallets: 10,
        stopId: 's2-34',
        status: 'delivered',
      },
      {
        id: 'c3-34',
        bol: 'BOL-37292',
        commodity: 'Roof Vents',
        weight: 14200,
        pallets: 8,
        stopId: 's3-34',
        status: 'delivered',
      },
      {
        id: 'c4-34',
        bol: 'BOL-37293',
        commodity: 'Gutter Systems',
        weight: 11800,
        pallets: 6,
        stopId: 's4-34',
        status: 'in-transit',
      },
    ],
  },
  {
    id: 'd-gen-35',
    name: 'Brandon Wilson',
    truckId: 'T-2768',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 0.4,
    location: { x: 30, y: 58 },
    route: [
      {
        id: 's1-35',
        address: '5700 Westpark Dr',
        eta: '12:30',
        window: '12:00 - 14:00',
        status: 'pending',
      },
      {
        id: 's2-35',
        address: '8400 Richmond Ave',
        eta: '14:45',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's3-35',
        address: '3300 Sage Rd',
        eta: '17:00',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-35',
        bol: 'BOL-85140',
        commodity: 'Siding — Vinyl',
        weight: 32600,
        pallets: 20,
        stopId: 's1-35',
        status: 'in-transit',
      },
      {
        id: 'c2-35',
        bol: 'BOL-85141',
        commodity: 'Trim Boards',
        weight: 27800,
        pallets: 16,
        stopId: 's2-35',
        status: 'loaded',
      },
      {
        id: 'c3-35',
        bol: 'BOL-85142',
        commodity: 'Corner Posts',
        weight: 23400,
        pallets: 13,
        stopId: 's3-35',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-36',
    name: 'Benjamin Anderson',
    truckId: 'T-8195',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'driving',
    hosDriven: 4.1,
    location: { x: 42, y: 32 },
    route: [
      {
        id: 's1-36',
        address: '3400 Woodway Dr',
        eta: '09:15',
        window: '09:00 - 11:00',
        status: 'completed',
      },
      {
        id: 's2-36',
        address: '6100 Voss Rd',
        eta: '11:30',
        window: '11:00 - 13:00',
        status: 'pending',
      },
      {
        id: 's3-36',
        address: '8700 Gessner Rd',
        eta: '13:45',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's4-36',
        address: '2900 Beltway 8',
        eta: '16:00',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-36',
        bol: 'BOL-52840',
        commodity: 'Decking — Composite',
        weight: 13600,
        pallets: 7,
        stopId: 's1-36',
        status: 'delivered',
      },
      {
        id: 'c2-36',
        bol: 'BOL-52841',
        commodity: 'Deck Railings',
        weight: 12200,
        pallets: 6,
        stopId: 's2-36',
        status: 'in-transit',
      },
      {
        id: 'c3-36',
        bol: 'BOL-52842',
        commodity: 'Deck Screws (Bulk)',
        weight: 10400,
        pallets: 5,
        stopId: 's3-36',
        status: 'loaded',
      },
      {
        id: 'c4-36',
        bol: 'BOL-52843',
        commodity: 'Post Caps',
        weight: 8600,
        pallets: 4,
        stopId: 's4-36',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-37',
    name: 'Victor Cruz',
    truckId: 'T-2198',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 30, y: 50 },
    route: [
      {
        id: 's1-37',
        address: '1900 Post Oak Blvd',
        eta: '14:00',
        window: '13:00 - 15:00',
        status: 'pending',
      },
      {
        id: 's2-37',
        address: '4500 Westheimer Rd',
        eta: '16:00',
        window: '15:00 - 17:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-37',
        bol: 'BOL-61200',
        commodity: 'Office Furniture',
        weight: 12400,
        pallets: 6,
        stopId: 's1-37',
        status: 'loaded',
      },
      {
        id: 'c2-37',
        bol: 'BOL-61201',
        commodity: 'Filing Cabinets',
        weight: 8800,
        pallets: 4,
        stopId: 's2-37',
        status: 'loaded',
      },
    ],
  },
  {
    id: 'd-gen-38',
    name: 'Derek Washington',
    truckId: 'T-5471',
    truckHeight: '13\'6"',
    status: 'normal',
    activityStatus: 'rest',
    hosDriven: 0,
    location: { x: 55, y: 72 },
    route: [
      {
        id: 's1-38',
        address: '2200 Shepherd Dr',
        eta: '14:30',
        window: '14:00 - 16:00',
        status: 'pending',
      },
      {
        id: 's2-38',
        address: '6100 Kirby Dr',
        eta: '16:30',
        window: '16:00 - 18:00',
        status: 'pending',
      },
    ],
    cargo: [
      {
        id: 'c1-38',
        bol: 'BOL-61300',
        commodity: 'Restaurant Equipment',
        weight: 15600,
        pallets: 8,
        stopId: 's1-38',
        status: 'loaded',
      },
      {
        id: 'c2-38',
        bol: 'BOL-61301',
        commodity: 'Kitchen Supplies',
        weight: 9200,
        pallets: 5,
        stopId: 's2-38',
        status: 'loaded',
      },
    ],
  },
]

// Assign signal bars: ~20% get 3 bars, rest get 4, Sarah already has 0
const generatedWithSignal = generatedDrivers.map((d, i) => ({
  ...d,
  signalBars: d.signalBars ?? (i % 5 === 0 ? 3 : 4),
}))

export const mockDrivers: Driver[] = [...baseDrivers, ...generatedWithSignal]

// Generate deliveries from driver data
let deliveryCounter = 10001
const routeDeliveries = mockDrivers.flatMap((driver) =>
  driver.route
    .filter((stop) => stop.address !== 'Return to Depot')
    .map((stop, idx) => {
      const cargo = driver.cargo[idx] || driver.cargo[0]
      return {
        id: `del-${driver.id}-${stop.id}`,
        refNumber: `DEL-${deliveryCounter++}`,
        status: stop.status,
        destination: stop.address,
        eta: stop.eta,
        window: stop.window,
        commodity: cargo?.commodity || 'General Freight',
        weight: cargo?.weight || 0,
        pallets: cargo?.pallets || 0,
        driverId: driver.id,
        driverName: driver.name,
        truckId: driver.truckId,
      }
    }),
)

const existingCompleted = routeDeliveries.filter(
  (d) => d.status === 'completed',
).length
const targetTotal = 1024
const targetCompleted = 347
const neededExtra = targetTotal - routeDeliveries.length
const neededCompleted = Math.max(0, targetCompleted - existingCompleted)
const neededPending = neededExtra - neededCompleted

const padAddresses = [
  '1200 Commerce St',
  '4500 Kirby Dr',
  '800 Travis St',
  '2100 Post Oak Blvd',
  '3300 Fannin St',
  '900 Louisiana St',
  '5600 Richmond Ave',
  '1400 Smith St',
  '7200 Bellaire Blvd',
  '2800 Shepherd Dr',
  '6100 Hillcroft Ave',
  '3900 Westpark Dr',
  '1050 Heights Blvd',
  '4700 Navigation Blvd',
  '8300 Long Point Rd',
  '2200 Yale St',
]
const padCommodities = [
  'General Freight',
  'Electronics',
  'Auto Parts',
  'Building Materials',
  'Food & Beverage',
  'Medical Supplies',
  'Industrial Equipment',
  'Retail Goods',
  'Chemicals (Non-Haz)',
  'Paper Products',
  'Textiles',
  'Furniture',
]
const padDriverIds = mockDrivers.map((d) => d.id)

const extraDeliveries: Delivery[] = []
for (let i = 0; i < neededExtra; i++) {
  const isCompleted = i < neededCompleted
  const driverIdx = i % padDriverIds.length
  const hr = 6 + (i % 12)
  const min = (i * 7) % 60
  extraDeliveries.push({
    id: `del-pad-${i}`,
    refNumber: `DEL-${deliveryCounter++}`,
    status: isCompleted ? 'completed' : i % 11 === 0 ? 'at-risk' : 'pending',
    destination: padAddresses[i % padAddresses.length],
    eta: `${String(hr).padStart(2, '0')}:${String(min).padStart(2, '0')}`,
    window: `${String(hr).padStart(2, '0')}:00 - ${String(hr + 2).padStart(2, '0')}:00`,
    commodity: padCommodities[i % padCommodities.length],
    weight: 2000 + ((i * 317) % 18000),
    pallets: 1 + (i % 12),
    driverId: padDriverIds[driverIdx],
    driverName: mockDrivers[driverIdx].name,
    truckId: mockDrivers[driverIdx].truckId,
  })
}

export const mockDeliveries: Delivery[] = [
  ...routeDeliveries,
  ...extraDeliveries,
]

export const mockAlerts: Alert[] = [
  {
    id: 'a-hos-marcus',
    driverId: 'd-marcus',
    severity: 'critical',
    title: 'HOS Violation Imminent',
    description: '1 delivery remaining • Depot 15 mi',
    timeContext: '30 min left',
    actionHint: 'Reassign stops',
    type: 'hos',
  },
  {
    id: 'a-brg-david',
    driverId: 'd-david',
    severity: 'critical',
    title: 'Low Bridge Ahead',
    description: 'Industrial Blvd bridge 12\'8" • Truck clearance 13\'6"',
    timeContext: 'ETA 15m',
    actionHint: 'Reroute',
    type: 'bridge',
  },
  {
    id: 'a-off-sarah',
    driverId: 'd-sarah',
    severity: 'warning',
    title: 'Intermittent Signal',
    description: 'T-0921 GPS dropping intermittently • Position data delayed',
    timeContext: 'Last drop 4m ago',
    actionHint: 'Contact driver',
    type: 'offline',
  },
  {
    id: 'a-traf-james',
    driverId: 'd-james',
    severity: 'warning',
    title: 'Traffic Delay',
    description: 'Accident on I-45 • +15 min to ETA',
    timeContext: '+15m ETA',
    actionHint: 'Reroute',
    type: 'traffic',
    affectedDriverIds: ['d-james', 'd-gen-2', 'd-gen-7', 'd-gen-14'],
  },
  {
    id: 'a-tire-elena',
    driverId: 'd-elena',
    severity: 'warning',
    title: 'Tire Pressure Low',
    description: 'T-3310 front-left 82 PSI • Min threshold 95 PSI',
    timeContext: '8m ago',
    actionHint: 'Schedule service',
    type: 'general',
  },

  {
    id: 'a-weather-region',
    driverId: 'd-gen-0',
    severity: 'warning',
    title: 'Weather Advisory',
    description:
      'Severe thunderstorm warning I-10 corridor • High winds 45+ mph',
    timeContext: 'Next 2 hrs',
    actionHint: 'No action needed',
    type: 'general',
    affectedDriverIds: [
      'd-gen-0',
      'd-gen-5',
      'd-gen-11',
      'd-gen-19',
      'd-gen-22',
      'd-gen-30',
    ],
  },
  {
    id: 'a-early-elena',
    driverId: 'd-elena',
    severity: 'info',
    title: 'Ahead of Schedule',
    description: 'T-3310 running 25 min early • Next window opens 09:00',
    timeContext: '25m early',
    actionHint: 'No action needed',
    type: 'general',
  },
  {
    id: 'a-maint-james',
    driverId: 'd-james',
    severity: 'info',
    title: 'Maintenance Due',
    description: 'T-1105 at 149,820 mi • Oil + brake inspection due at 150k',
    timeContext: '~180 mi',
    actionHint: 'Schedule service',
    type: 'general',
  },
  {
    id: 'a-weather-david',
    driverId: 'd-david',
    severity: 'info',
    title: 'Weather Advisory',
    description:
      'Thunderstorm on I-10 corridor 15:00–18:00 • 3 trucks affected',
    timeContext: 'In 2h',
    actionHint: 'Monitor conditions',
    type: 'traffic',
  },
]

export const kpis = {
  activeDrivers: { current: 42, total: 50 },
  deliveries: { current: 347, total: 1024 },
  onTimeRate: 94.2,
  activeAlerts: 7,
  avgEtaAccuracy: '+4 min',
}

export interface TriageAction {
  id: string
  label: string
  type: 'primary' | 'secondary' | 'dismiss'
}

export interface TriageAlternative {
  id: string
  label: string
  description: string
}

export interface TriageItem {
  id: string
  priority: number // 1-5, 1 is most urgent
  alertId?: string
  driverId: string
  issue: string
  recommendation: string
  confidence: 'high' | 'medium'
  actions: TriageAction[]
  alternatives: TriageAlternative[]
  status: 'pending' | 'approved' | 'dismissed'
  category: 'reactive' | 'proactive'
}

export const mockTriageItems: TriageItem[] = [
  {
    id: 't-1',
    priority: 1,
    alertId: 'a-hos-marcus',
    driverId: 'd-marcus',
    category: 'reactive',
    issue:
      'Marcus Johnson is approaching mandatory HOS reset with 1 pending delivery. Depot is 15 mi away.',
    recommendation:
      'Skip final delivery and route directly to depot (15 mi, ~20 min). Reassign stop to Elena Rodriguez — she is 4 miles away with 8.9h HOS remaining.',
    confidence: 'high',
    status: 'pending',
    actions: [{ id: 'act-1', label: 'Approve Reassignment', type: 'primary' }],
    alternatives: [
      {
        id: 'alt-1',
        label: 'Reassign to James Wilson',
        description: 'James is 12 miles away (ETA +25m)',
      },
      {
        id: 'alt-2',
        label: 'Split Deliveries',
        description: 'Give 2 stops to Elena, 1 to James',
      },
    ],
  },
  {
    id: 't-2',
    priority: 2,
    alertId: 'a-brg-david',
    driverId: 'd-david',
    category: 'reactive',
    issue:
      'David Kim is on a route with a low bridge clearance (12\'8") ahead.',
    recommendation:
      'Approve alternate route via Highway 6. Adds 8 minutes to ETA but avoids hazard.',
    confidence: 'high',
    status: 'pending',
    actions: [{ id: 'act-2', label: 'Approve Reroute', type: 'primary' }],
    alternatives: [
      {
        id: 'alt-3',
        label: 'Reroute via FM 1960',
        description: 'Adds 12 minutes to ETA',
      },
      {
        id: 'alt-4',
        label: 'Hold and Contact',
        description: 'Instruct driver to pull over and call dispatch',
      },
    ],
  },
  {
    id: 't-3',
    priority: 2,
    alertId: 'a-off-sarah',
    driverId: 'd-sarah',
    category: 'reactive',
    issue: 'Lost GPS and telemetry signal for Sarah Chen (Truck T-0921).',
    recommendation:
      "Initiate automated check-in call to driver's mobile device.",
    confidence: 'medium',
    status: 'pending',
    actions: [{ id: 'act-3', label: 'Call Driver', type: 'primary' }],
    alternatives: [
      {
        id: 'alt-5',
        label: 'Dispatch Roadside',
        description: 'Send assistance to last known location',
      },
      {
        id: 'alt-6',
        label: 'Wait 5 Minutes',
        description: 'Snooze alert to see if signal returns',
      },
    ],
  },
  {
    id: 't-4',
    priority: 3,
    alertId: 'a-fuel-marcus',
    driverId: 'd-marcus',
    category: 'reactive',
    issue: 'Truck T-1042 fuel level is critical (12%).',
    recommendation:
      'Route to Pilot station on I-45. It is on the current path and adds only 3 mins to ETA.',
    confidence: 'high',
    status: 'pending',
    actions: [{ id: 'act-4', label: 'Route to Pilot', type: 'primary' }],
    alternatives: [
      {
        id: 'alt-7',
        label: "Route to Buc-ee's",
        description: 'Adds 7 mins to ETA, but fuel is $0.12/gal cheaper',
      },
      {
        id: 'alt-8',
        label: 'Continue Route',
        description: 'Refuel at next scheduled stop (risky)',
      },
    ],
  },
  {
    id: 't-5',
    priority: 3,
    driverId: 'd-james',
    category: 'proactive',
    issue:
      'James Wilson approaching 8h mark with heavy traffic detected ahead on I-45.',
    recommendation:
      'Schedule preemptive 30-minute break at the upcoming rest stop before hitting traffic.',
    confidence: 'medium',
    status: 'pending',
    actions: [{ id: 'act-5', label: 'Schedule Break', type: 'primary' }],
    alternatives: [
      {
        id: 'alt-9',
        label: 'Continue Route',
        description: 'Push through traffic (high risk of delay)',
      },
      {
        id: 'alt-10',
        label: 'Reassign Stops',
        description: 'Offload downstream deliveries now',
      },
    ],
  },
  {
    id: 't-6',
    priority: 4,
    driverId: 'd-elena',
    category: 'proactive',
    issue: 'Elena Rodriguez is running 25 minutes ahead of schedule.',
    recommendation:
      'Pull forward the next delivery window and notify the customer of early arrival.',
    confidence: 'high',
    status: 'pending',
    actions: [
      { id: 'act-6', label: 'Update Window & Notify', type: 'primary' },
    ],
    alternatives: [
      {
        id: 'alt-11',
        label: 'Keep Schedule',
        description: 'Driver will wait at location',
      },
      {
        id: 'alt-12',
        label: 'Assign Pickup',
        description: 'Add a nearby backhaul pickup to route',
      },
    ],
  },
  {
    id: 't-7',
    priority: 3,
    alertId: 'a-tire-elena',
    driverId: 'd-elena',
    category: 'reactive',
    issue: 'Front-left tire on Truck T-3310 is reading low pressure (82 PSI).',
    recommendation:
      'Schedule service at depot after current route completes (safe to finish shift).',
    confidence: 'high',
    status: 'pending',
    actions: [
      { id: 'act-7', label: 'Schedule Depot Service', type: 'primary' },
    ],
    alternatives: [
      {
        id: 'alt-13',
        label: 'Divert to Service Center',
        description: 'Fix immediately (delays route by 1.5h)',
      },
      {
        id: 'alt-14',
        label: 'Monitor Only',
        description: 'Take no action unless pressure drops further',
      },
    ],
  },
]
